import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Languages, Heart, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../services/api';

export default function CountryGuidesPage() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGuides();
  }, []);

  const loadGuides = async () => {
    try {
      const response = await contentAPI.countryGuides();
      setGuides(response.data.guides || []);
    } catch (error) {
      console.error('Failed to load guides:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-12" data-testid="country-guides-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-6 py-4">
          <button
            onClick={() => selectedGuide ? setSelectedGuide(null) : navigate(-1)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-heading text-2xl text-white">
              {selectedGuide ? selectedGuide.country : 'Country Guides'}
            </h1>
            <p className="text-sm text-gray-400 font-body">
              {selectedGuide ? selectedGuide.region : 'Dating culture across Africa'}
            </p>
          </div>
        </div>
      </header>

      {selectedGuide ? (
        /* Country Detail */
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="px-6 py-6"
        >
          {/* Flag & Name */}
          <div className="text-center mb-8">
            <span className="text-6xl mb-4 block">{selectedGuide.flag_emoji}</span>
            <h2 className="font-heading text-3xl text-white">{selectedGuide.country}</h2>
            <p className="text-gold font-body">{selectedGuide.region}</p>
          </div>

          {/* Languages */}
          <div className="bg-dark-paper border border-white/5 rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Languages className="w-5 h-5 text-gold" />
              <h3 className="font-body font-semibold text-white">Languages</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedGuide.languages.map(lang => (
                <span key={lang} className="px-3 py-1 bg-dark-elevated text-gray-300 text-sm rounded-full font-body">
                  {lang}
                </span>
              ))}
            </div>
          </div>

          {/* Dating Norms */}
          <div className="bg-dark-paper border border-white/5 rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Heart className="w-5 h-5 text-gold" />
              <h3 className="font-body font-semibold text-white">Dating Norms</h3>
            </div>
            <p className="text-gray-300 font-body leading-relaxed">
              {selectedGuide.dating_norms}
            </p>
          </div>

          {/* Cultural Tips */}
          <div className="bg-dark-paper border border-white/5 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="w-5 h-5 text-gold" />
              <h3 className="font-body font-semibold text-white">Cultural Tips</h3>
            </div>
            <ul className="space-y-2">
              {selectedGuide.cultural_tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gold text-xs">{index + 1}</span>
                  </span>
                  <p className="text-gray-300 font-body text-sm">{tip}</p>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      ) : (
        /* Country List */
        <div className="px-6 py-6">
          <p className="text-gray-400 font-body mb-6">
            Learn about dating culture and customs in different African countries
          </p>

          <div className="space-y-3">
            {guides.map((guide, index) => (
              <motion.button
                key={guide.country}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedGuide(guide)}
                className="w-full flex items-center gap-4 p-4 bg-dark-paper border border-white/5 rounded-xl hover:border-gold/30 transition-colors text-left"
                data-testid={`guide-${guide.country.toLowerCase()}`}
              >
                <span className="text-3xl">{guide.flag_emoji}</span>
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-white">{guide.country}</h3>
                  <p className="text-sm text-gray-400">{guide.region}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
